/**
 * React glue around the pure {@link ./railStackLayout} policy.
 *
 * `RightRailContent` computes the layout once and provides it here; every rail
 * card (inline in `RightRail` and the standalone Booking* section components)
 * reads its own render decisions via {@link useRailStackPanel}. This keeps all
 * "how does a card share the rail height" logic in one place.
 */
import { createContext, useContext, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  type RailPanelLayout,
  type RailStackLayout,
  type RailStackMode,
  type RailStackPanelId,
} from "./railStackLayout";

/** Per-card view model consumed by `CollapsibleRailSection` + its wrapper. */
export interface RailPanelView extends RailPanelLayout {
  mode: RailStackMode;
  /** `CollapsibleRailSection` `expanded`. */
  expanded: boolean;
  /** Header-pinned collapsed card (desktop, closed). */
  accordion: boolean;
  /** Open card flexes to share the rail height (desktop, open). */
  fill: boolean;
  /** Mobile drawer flow — size to content. */
  flow: boolean;
  /** Scroll the body inline (open desktop cards except Kai, which self-scrolls). */
  scroll: boolean;
}

const RailStackContext = createContext<RailStackLayout | null>(null);

export function RailStackProvider({
  layout,
  children,
}: {
  layout: RailStackLayout;
  children: ReactNode;
}) {
  return (
    <RailStackContext.Provider value={layout}>
      {children}
    </RailStackContext.Provider>
  );
}

/** Content wrapper inside {@link CollapsibleRailSection} when the card shares stack height. */
export function railStackSectionContentClassName(
  stackGrow: boolean,
  className?: string,
): string {
  return cn(
    className,
    stackGrow && "min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden",
  );
}

/** Card uses an inner body scroller when sharing stack height (not section-inner). */
export function railStackInlineScrollRootClassName(
  stackGrow: boolean,
  className?: string,
): string {
  return cn(
    className,
    stackGrow &&
      "rail-stack-inline-scroll-root min-h-0 min-w-0 flex flex-1 flex-col overflow-hidden",
  );
}

export function railStackInlineScrollBodyClassName(
  stackGrow: boolean,
  className?: string,
): string {
  return cn(
    className,
    stackGrow &&
      "rail-stack-inline-scroll-body min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
  );
}

/** Portaled rail body root (Opportunities, lookup, B/L, …). */
export function railStackPortalRootClassName(
  stack: boolean,
  className?: string,
): string {
  return cn(
    className,
    stack && "rail-stack-portal-root min-h-0 min-w-0 flex-1 flex flex-col",
  );
}

/** CollapsibleRailSection wrapper classes for desktop stack + stretch targets. */
export function railSectionStackClassName(
  view: RailPanelView,
  options: {
    /** Mobile drawer `stack` prop on RightRailContent. */
    mobileStack?: boolean;
    stackAnimate?: boolean;
    stackGrow?: boolean;
  } = {},
): string {
  const { mobileStack = false, stackAnimate = false, stackGrow = false } =
    options;
  return cn(
    !mobileStack && "min-h-0 overflow-hidden",
    stackAnimate && stackGrow && view.fill && "flex min-h-0 flex-1 flex-col",
  );
}

/** Derive a card's full view model from the shared layout. */
export function railPanelView(
  layout: RailStackLayout,
  id: RailStackPanelId,
): RailPanelView {
  const panel = layout.panels[id];
  const stacked = layout.mode === "stacked";
  return {
    ...panel,
    mode: layout.mode,
    expanded: panel.bodyOpen,
    accordion: stacked ? !panel.bodyOpen : false,
    fill: stacked ? panel.bodyOpen : false,
    flow: layout.mode === "mobileFlow",
    scroll: stacked && panel.bodyOpen && id !== "kai",
  };
}

/** Read one card's view model. Falls back to a collapsed desktop card. */
export function useRailStackPanel(id: RailStackPanelId): RailPanelView {
  const layout = useContext(RailStackContext);
  if (!layout) {
    return {
      present: false,
      bodyOpen: false,
      fill: false,
      focused: false,
      mode: "stacked",
      expanded: false,
      accordion: true,
      flow: false,
      scroll: false,
    };
  }
  return railPanelView(layout, id);
}

/** Glass-card wrapper class that never changes the rail height when toggled. */
export function railCardClassName(
  view: RailPanelView,
  options?: {
    pinBottom?: boolean;
    pinExpanded?: boolean;
    /** Absorb slack above pinned Notes (desktop stack). */
    grow?: boolean;
  },
): string {
  if (view.flow) return "flex w-full flex-col";
  const stacked = view.mode === "stacked";
  const pinBottom = options?.pinBottom ?? false;
  const pinExpanded = options?.pinExpanded ?? false;
  const grow = options?.grow ?? false;
  return cn(
    "rail-workspace-card glass-panel relative isolate flex w-full flex-col overflow-clip p-4",
    stacked
      ? cn(
          "min-h-0",
          grow || (pinBottom && pinExpanded)
            ? "rail-stack-card--stretch"
            : "rail-stack-card--shrink",
          view.fill && "rail-stack-card--open",
        )
      : view.fill
        ? "min-h-0 flex-1"
        : "shrink-0 flex-none",
  );
}

/** Inner flex column wrapper used inside each card. */
export function railCardInnerClassName(
  view: RailPanelView,
  options?: {
    pinBottom?: boolean;
    pinExpanded?: boolean;
    grow?: boolean;
  },
): string {
  const pinExpanded = options?.pinExpanded ?? false;
  const grow = options?.grow ?? false;
  return view.flow
    ? "flex w-full min-w-0 flex-col"
    : cn(
        "flex min-w-0 flex-col",
        (view.fill && view.mode !== "stacked") ||
          (view.mode === "stacked" && (grow || pinExpanded))
          ? "min-h-0 flex-1"
          : undefined,
      );
}
